import { Box } from "@mui/material";
import { TextFieldProps } from "@mui/material/TextField";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { format, isToday, addDays, isBefore, startOfDay } from "date-fns";
import { FC, useMemo, useEffect, useCallback, useState } from "react";
import Select from "shared/components/Form/Select";
import FieldLabel from "shared/components/Form/components/FieldLabel";
import { COLOR_BLACK } from "shared/theme/colors";
import { CalendarIcon } from "shared/theme/icons/CalendarIcon";
import { generateTimes, getTimezones } from "./helpers";
import {
  StyledDateTextField,
  TimeSelect,
  StyledPopper,
  StyledContainer,
} from "./styled";

export type DateTimePickerProps = {
  date: Date | null;
  time: string | null;
  timezone: string;
  onDateChange: (date: Date | null) => void;
  onTimeChange: (time: string) => void;
  onTimezoneChange: (timezone: string) => void;
  onCombinedDateTimeChange?: (combinedDateTime: string) => void;
  disabled?: boolean;
  dateDisplayFormat?: string;
  maxDaysInFuture?: number;
  minDate?: Date;
  minTime?: string | null;
  dateLabel?: string;
  formatCombinedDateTime?: (
    date: Date,
    time: string,
    timezone: string,
  ) => string;
};

const DateTimePicker: FC<DateTimePickerProps> = ({
  date,
  time,
  timezone,
  onDateChange,
  onTimeChange,
  onTimezoneChange,
  onCombinedDateTimeChange,
  disabled = false,
  dateDisplayFormat = "do MMM yyyy",
  maxDaysInFuture = 30,
  minDate = new Date(),
  minTime = null,
  dateLabel = "Date",
  formatCombinedDateTime,
}) => {
  const [userSelectedDate, setUserSelectedDate] = useState<boolean>(false);

  const timezones = useMemo(() => getTimezones(), []);
  const calculatedMaxDate = useMemo(
    () => addDays(new Date(), maxDaysInFuture),
    [maxDaysInFuture],
  );

  const updateCombinedDateTime = useCallback(
    (
      currentDate: Date | null,
      currentTime: string | null,
      currentTimezone: string,
    ) => {
      if (
        currentDate &&
        currentTime &&
        currentTimezone &&
        onCombinedDateTimeChange
      ) {
        if (formatCombinedDateTime) {
          onCombinedDateTimeChange(
            formatCombinedDateTime(currentDate, currentTime, currentTimezone),
          );
        } else {
          const year = currentDate.getFullYear();
          const month = String(currentDate.getMonth() + 1).padStart(2, "0");
          const day = String(currentDate.getDate()).padStart(2, "0");
          const dateStr = `${year}-${month}-${day}`;

          const combinedDateTime = `${dateStr} ${currentTime} ${currentTimezone}`;
          onCombinedDateTimeChange(combinedDateTime);
        }
      } else if (onCombinedDateTimeChange) {
        onCombinedDateTimeChange("");
      }
    },
    [onCombinedDateTimeChange, formatCombinedDateTime],
  );

  const isTimeValid = (
    date: Date,
    timeStr: string,
    timezoneStr: string,
  ): boolean => {
    if (!isToday(date)) return true;

    if (timezoneStr) return true;

    try {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();

      const nextMinuteIncrement = Math.ceil(currentMinute / 15) * 15;
      let nextMinute;
      if (nextMinuteIncrement <= currentMinute) {
        nextMinute = nextMinuteIncrement + 15;
      } else {
        nextMinute = nextMinuteIncrement;
      }

      const nextHour = currentHour + Math.floor(nextMinute / 60);
      const normalizedNextMinute = nextMinute % 60;

      const [selectedHourStr, selectedMinuteStr] = timeStr.split(":");
      const selectedHour = parseInt(selectedHourStr);
      const selectedMinute = parseInt(selectedMinuteStr);

      return (
        selectedHour > nextHour ||
        (selectedHour === nextHour && selectedMinute >= normalizedNextMinute)
      );
    } catch (e) {
      console.error("Error validating time:", e);
      return false;
    }
  };

  useEffect(() => {
    if (date && time && userSelectedDate) {
      if (!isTimeValid(date, time, timezone)) {
        onTimeChange("");
        updateCombinedDateTime(date, "", timezone);
      }
    }
  }, [
    date,
    timezone,
    time,
    onTimeChange,
    updateCombinedDateTime,
    userSelectedDate,
  ]);

  const handleTimezoneChange = (event: string) => {
    onTimezoneChange(event);

    // If date is today and time is selected, check if it's valid in the new timezone
    if (date && time && userSelectedDate) {
      if (!isTimeValid(date, time, event)) {
        onTimeChange("");
        updateCombinedDateTime(date, "", event);
        return;
      }
    }

    updateCombinedDateTime(date, time, event);
  };

  const handleDateChange = (newDate: Date | null) => {
    if (newDate) {
      setUserSelectedDate(true);

      // Check if selected date is in the past
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to beginning of day for comparison
      const selectedDateCopy = new Date(newDate);
      selectedDateCopy.setHours(0, 0, 0, 0);

      // If selected date is in the past, clear the time
      if (selectedDateCopy < today && time) {
        onTimeChange("");
        onDateChange(newDate);
        updateCombinedDateTime(newDate, "", timezone);
        return;
      }
    }

    onDateChange(newDate);

    // Validate time for the new date
    if (newDate && time && userSelectedDate) {
      if (!isTimeValid(newDate, time, timezone)) {
        onTimeChange("");
      }
    }

    updateCombinedDateTime(newDate, time, timezone);
  };

  const handleTimeChange = (newTime: string) => {
    const selectedTimeOption = timeOptions.find(
      (option) => option.value === newTime,
    );
    if (selectedTimeOption?.disabled) {
      return;
    }

    if (!date && newTime) {
      const today = new Date();
      if (minDate && isBefore(today, startOfDay(minDate))) {
        onDateChange(minDate);
      } else {
        onDateChange(today);
      }
      setUserSelectedDate(true);
      onTimeChange(newTime);
      updateCombinedDateTime(date ?? today, newTime, timezone);
      return;
    }

    onTimeChange(newTime);
    updateCombinedDateTime(date, newTime, timezone);
  };

  const timeOptions = useMemo(
    () => generateTimes(date, timezone, minTime),
    [date, timezone, minTime],
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <StyledContainer>
        <Box display="flex" flexDirection="column">
          <FieldLabel label={dateLabel} labelColor={COLOR_BLACK} />
          <DatePicker
            value={date}
            onChange={handleDateChange}
            components={{
              OpenPickerIcon: CalendarIcon,
            }}
            minDate={minDate}
            maxDate={calculatedMaxDate}
            views={["year", "month", "day"]}
            disabled={disabled}
            renderInput={(params: TextFieldProps) => (
              <StyledDateTextField
                {...params}
                inputProps={{
                  ...params.inputProps,
                  value: date ? format(date, dateDisplayFormat) : "",
                  readOnly: true,
                  placeholder: "Select date",
                }}
                disabled={disabled}
                sx={{ cursor: "pointer" }}
              />
            )}
            PopperProps={{
              sx: StyledPopper,
            }}
          />
        </Box>

        <TimeSelect
          id="time"
          label="Time"
          labelColor={COLOR_BLACK}
          placeholder="Select time"
          disabledPlaceholder
          fullWidth
          required
          value={time ?? undefined}
          handleChange={handleTimeChange}
          options={timeOptions}
          disabled={disabled}
        />

        <Select
          id="timezone"
          label="Timezone"
          labelColor={COLOR_BLACK}
          placeholder="Select timezone"
          disabledPlaceholder
          fullWidth
          required
          value={timezone ?? undefined}
          handleChange={handleTimezoneChange}
          options={timezones}
          disabled={disabled}
        />
      </StyledContainer>
    </LocalizationProvider>
  );
};

export default DateTimePicker;
